import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { TextField } from './inputs/TextField';
import type { PermissionCatalogItem, PermissionCatalogModule } from '../features/administration/roles/roleTypes';
import { Checkbox } from './inputs/Checkbox';

interface PermissionPickerProps {
  /** The full server-side catalog, grouped by module. */
  catalog: PermissionCatalogModule[];
  /** Checked permission keys. */
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Restricts what may be offered at all. Anything outside it is dropped before rendering,
   * so a surface bounded by a role cannot show — let alone tick — a key the role never
   * grants. Omit to offer the whole catalog.
   */
  limitToKeys?: string[];
  disabled?: boolean;
  /** Prefix for every generated DOM id. Must be unique on the page. */
  idPrefix: string;
  /** Shown above the search box when there is something worth saying. */
  description?: string;
  /** Shown when the bounded catalog is empty — a different case from a search miss. */
  emptyMessage?: string;
  /**
   * Opens showing only the modules this account actually holds something in, with a control
   * to reveal the rest.
   *
   * For a READ-ONLY view of what an account has. A profile answers "what can this person
   * do", and the whole catalog answers it by burying eighteen enabled rows in two hundred
   * empty ones — the modules the account holds nothing in are the ones it is not about.
   *
   * Never on an editing surface. Granting a permission means finding a module that is
   * currently empty, so hiding the empty ones would hide everything left to grant.
   */
  showOnlyEnabled?: boolean;
  /**
   * Draws a vertical SUITE rail to the left of the module list, narrowing it to one suite.
   *
   * Opt-in, because it earns its width only on a surface showing the WHOLE catalogue — 376
   * permissions across a dozen suites, where scrolling is the only way to reach the last one.
   * A picker bounded by `limitToKeys` to a single role's grants, or one on a profile showing
   * only what an account holds, is a short list the rail would merely narrow the content of.
   *
   * It is still DERIVED on top of that: with one suite in scope there is nothing to choose
   * between, so no rail renders whatever this says.
   */
  suiteFilter?: boolean;
}

/**
 * Searchable permission picker — the one place permissions are chosen.
 *
 * Both surfaces that grant permissions use it: the role editor over the whole catalog, and
 * the create-user form bounded to a single role via `limitToKeys`.
 *
 * **One table per module.** A single table with module rows threaded through it made the
 * header stop applying halfway down and left no obvious place for a module's own
 * description or its bulk toggle. Each module is its own bordered table with its own header,
 * so the columns always mean what the header says and a module can be read on its own.
 * Modules are grouped under their suite.
 */
export function PermissionPicker({
  catalog,
  value,
  onChange,
  limitToKeys,
  disabled = false,
  idPrefix,
  description,
  emptyMessage = 'This role grants no permissions yet.',
  showOnlyEnabled = false,
  suiteFilter = false,
}: PermissionPickerProps) {
  const [query, setQuery] = useState('');
  const [revealAll, setRevealAll] = useState(false);
  const [suite, setSuite] = useState<string | null>(null);

  const allowed = useMemo(() => (limitToKeys ? new Set(limitToKeys) : null), [limitToKeys]);
  const checked = useMemo(() => new Set(value), [value]);

  // The bounded catalog: what this surface may offer at all, before any search.
  const scoped = useMemo(() => {
    if (!allowed) return catalog;

    return catalog
      .map((module) => ({
        ...module,
        permissions: module.permissions.filter((p) => allowed.has(p.key)),
      }))
      .filter((module) => module.permissions.length > 0);
  }, [catalog, allowed]);

  const offered = useMemo(
    () => scoped.flatMap((module) => module.permissions.map((p) => p.key)),
    [scoped],
  );

  /*
    The modules this account actually holds something in.

    A whole module is kept or dropped — never individual rows. A module the account holds one
    of four permissions in still shows all four, because "what can this person do here" is
    answered by the three that are off as much as by the one that is on. It is the modules
    holding nothing that say nothing.
  */
  const inScope = useMemo(() => {
    if (!showOnlyEnabled || revealAll) return scoped;

    return scoped.filter((module) => module.permissions.some((p) => checked.has(p.key)));
  }, [scoped, showOnlyEnabled, revealAll, checked]);

  const hiddenModuleCount = scoped.length - inScope.length;

  const term = query.trim().toLowerCase();

  const visible = useMemo(() => {
    if (!term) return inScope;

    const matches = (p: PermissionCatalogItem, module: PermissionCatalogModule) =>
      p.name.toLowerCase().includes(term) ||
      p.key.toLowerCase().includes(term) ||
      (p.description?.toLowerCase().includes(term) ?? false) ||
      module.name.toLowerCase().includes(term) ||
      (module.description?.toLowerCase().includes(term) ?? false);

    return inScope
      .map((module) => ({
        ...module,
        permissions: module.permissions.filter((p) => matches(p, module)),
      }))
      .filter((module) => module.permissions.length > 0);
  }, [inScope, term]);

  const bySuite = useMemo(() => {
    const groups = new Map<string, PermissionCatalogModule[]>();

    for (const module of visible) {
      groups.set(module.suite, [...(groups.get(module.suite) ?? []), module]);
    }

    return [...groups.entries()];
  }, [visible]);

  /*
    The rail's entries are what is SHOWABLE right now, so a search narrows the rail as well as
    the list — a suite offering no match is not a place worth sending anybody.

    It also means the chosen suite can vanish mid-search, so the selection is DERIVED rather
    than trusted: an active choice the rail can no longer offer falls back to All, which is the
    same correction the index page's folder strip makes. Storing it and rendering blindly would
    leave the reader staring at an empty pane while matches sat one click away.
  */
  const railSuites = useMemo(
    () => bySuite.map(([name, modules]) => ({
      name,
      enabled: modules.reduce(
        (total, module) => total + module.permissions.filter((p) => checked.has(p.key)).length,
        0,
      ),
    })),
    [bySuite, checked],
  );

  const showRail = suiteFilter && railSuites.length > 1;
  const activeSuite = showRail && railSuites.some((entry) => entry.name === suite) ? suite : null;

  const shownSuites = activeSuite
    ? bySuite.filter(([name]) => name === activeSuite)
    : bySuite;

  function setKeys(keys: string[], on: boolean) {
    const next = new Set(checked);

    for (const key of keys) {
      if (on) next.add(key);
      else next.delete(key);
    }

    onChange([...next]);
  }

  const chosenCount = offered.filter((key) => checked.has(key)).length;
  const matchCount = visible.reduce((total, module) => total + module.permissions.length, 0);

  if (scoped.length === 0) {
    return <EmptyState message={emptyMessage} minHeight="min-h-[160px]" />;
  }

  return (
    <div className="flex flex-col gap-4">
      {description ? <p className="text-xs text-slate-500">{description}</p> : null}

      {/*
        Never disabled by `disabled`.

        `disabled` means the grants cannot be CHANGED — it belongs on the checkboxes and
        nowhere else. Search is how the list is read, and a read-only picker is still a thing
        someone reads: on a profile showing sixty permissions it is the only way to find one.
        Disabling it turned "you may not edit this" into "you may not look through this".
      */}
      <TextField
        id={`${idPrefix}-search`}
        label="Search permissions"
        type="search"
        autoComplete="off"
        placeholder="Filter by function, key, or description…"
        leading={<Search className="h-4 w-4" aria-hidden="true" />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {/*
        The count is of the WHOLE bounded catalog, not of what is on screen — it is the
        headline fact about the account, and it must not change when a module is hidden.
      */}
      <div className="flex min-w-0 items-center gap-3">
        <p className="min-w-0 flex-1 text-xs text-slate-500" aria-live="polite">
          {chosenCount} of {offered.length} enabled
          {term ? ` · ${matchCount} shown` : null}
        </p>

        {/*
          Reveal is offered only when something is actually hidden, and the way back is
          offered only once it has been taken — a control that would change nothing does not
          render. The label counts what it will show, so the reader knows what they are
          asking for before they ask.
        */}
        {showOnlyEnabled && (hiddenModuleCount > 0 || revealAll) ? (
          <Button
            id={`${idPrefix}-reveal-all`}
            variant="tertiary"
            className="shrink-0 px-2 py-1 text-xs"
            onClick={() => setRevealAll((shown) => !shown)}
          >
            {revealAll
              ? 'Show only enabled'
              : `Show all ${scoped.length} module${scoped.length === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </div>

      {bySuite.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
          {term
            ? 'No permission matches that search.'
            : /*
                Not a search miss: the account holds nothing anywhere. Said plainly, because
                an empty panel under a "0 of 42 enabled" line reads as a failed render.
              */
              'This account holds no permissions.'}
        </p>
      ) : (
        <div className="flex min-w-0 items-start gap-4">
          {showRail ? (
            <SuiteRail
              idPrefix={idPrefix}
              suites={railSuites}
              active={activeSuite}
              onSelect={setSuite}
            />
          ) : null}

          {/*
            `min-w-0` is what stops a module table's own 520px minimum from pushing the rail
            off: a flex child defaults to `min-width: auto`, so without it the content refuses
            to shrink and the row grows instead of the table scrolling inside itself.
          */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {shownSuites.map(([name, modules]) => (
              <section key={name} className="flex flex-col gap-3">
                {/*
                  The heading stays even with one suite chosen. It names what is on screen, and
                  a pane that drops its own title once filtered makes the rail the only thing
                  saying where the reader is.
                */}
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {name}
                </h3>

                {modules.map((module) => (
                  <ModuleTable
                    key={module.key}
                    module={module}
                    checked={checked}
                    disabled={disabled}
                    idPrefix={idPrefix}
                    onSetKeys={setKeys}
                  />
                ))}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The suite rail — a vertical filter standing to the left of the module list.
 *
 * **A RAIL ADDS NO WEIGHT.** The chosen entry is `text-slate-900` on a tint and nothing else:
 * one bolded row in a column of short labels reads as a heading over the rows beneath it, which
 * is the mistake `frontend-core.md` records against both of the product's other rails.
 *
 * It leads with All, as every facet in the product does, and All carries no count — summing the
 * suites below it would be true only while they partition the list, and the figure a reader
 * wants here is the one beside each suite: how many permissions they have already granted in it.
 */
function SuiteRail({
  idPrefix,
  suites,
  active,
  onSelect,
}: {
  idPrefix: string;
  suites: { name: string; enabled: number }[];
  active: string | null;
  onSelect: (suite: string | null) => void;
}) {
  const row = (selected: boolean) =>
    [
      'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200',
      selected ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50',
    ].join(' ');

  /*
    STICKY, because the list it filters is the thing that scrolls.

    Left in the flow it scrolled away with the modules — so by the time a reader had gone far
    enough to want a different suite, the control for choosing one was off screen and the only
    way back was scrolling up through the list they were trying to skip. It sticks to the top of
    the scrolling body instead.

    Its cap is a fraction of the VIEWPORT rather than a fixed height, so it is bounded by what
    the reader can actually see. A magic `26rem` clipped the last suite mid-row on a tall window
    and would have had to be re-tuned the day a suite was added — the kind of number that is
    right only for the data it was measured against.
  */
  return (
    <nav
      aria-label="Filter by suite"
      className="modern-scrollbar sticky top-0 max-h-[60vh] w-44 shrink-0 overflow-y-auto overflow-x-hidden"
    >
      <ul className="flex flex-col gap-0.5">
        <li>
          <button
            type="button"
            id={`${idPrefix}-suite-all`}
            onClick={() => onSelect(null)}
            aria-current={active === null ? 'true' : undefined}
            className={row(active === null)}
          >
            All
          </button>
        </li>

        {suites.map((entry) => (
          <li key={entry.name}>
            <button
              type="button"
              id={`${idPrefix}-suite-${entry.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              onClick={() => onSelect(entry.name)}
              aria-current={active === entry.name ? 'true' : undefined}
              className={row(active === entry.name)}
            >
              <span className="min-w-0 flex-1 truncate">{entry.name}</span>

              {/* Only when there is something to report — a zero here says nothing a reader needs. */}
              {entry.enabled > 0 ? (
                <span className="shrink-0 font-mono tabular-nums text-[11px] text-slate-400">
                  {entry.enabled}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** One module, one table. Its header carries the module's own name, purpose, and bulk toggle. */
function ModuleTable({
  module,
  checked,
  disabled,
  idPrefix,
  onSetKeys,
}: {
  module: PermissionCatalogModule;
  checked: Set<string>;
  disabled: boolean;
  idPrefix: string;
  onSetKeys: (keys: string[], on: boolean) => void;
}) {
  const keys = module.permissions.map((p) => p.key);
  const chosen = keys.filter((k) => checked.has(k));
  const allOn = chosen.length === keys.length;

  return (
    <div
      id={`${idPrefix}-module-${module.key}-table`}
      className="overflow-hidden rounded-lg border border-slate-200"
    >
      {/*
        The module's name and purpose only. Its "All" toggle used to sit here as well, and is
        now the select-all at the head of the checkbox column — one control, in the standard
        place, rather than a second one a row above it.
      */}
      <header className="min-w-0 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {module.name}
        </p>
        {module.description ? (
          <p className="mt-0.5 text-xs text-slate-500">{module.description}</p>
        ) : null}
      </header>

      {/* Dense content stays inside its own scroller so the page never scrolls sideways. */}
      <div className="modern-scrollbar overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200">
              {/*
                The checkbox leads, as it does on every other table in the product — the
                standard gutter, ahead of the data columns. It used to sit last under an
                ENABLED heading, which put the one control on the row at the end of it and
                made this the only table where the ticks did not line up under the reader's
                eye on the way down.

                The header cell is the module's select-all, which is where a select-all
                belongs: at the head of the column it controls.
              */}
              <th scope="col" className="w-10 px-3 py-2">
                <Checkbox
                  id={`${idPrefix}-module-${module.key}`}
                  name={`${idPrefix}-module-${module.key}`}
                  checked={allOn}
                  indeterminate={chosen.length > 0 && !allOn}
                  onChange={() => onSetKeys(keys, !allOn)}
                  disabled={disabled}
                  aria-label={`Enable every ${module.name} permission`}
                />
              </th>
              <th
                scope="col"
                className="w-[34%] px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              >
                Function
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              >
                Description
              </th>
            </tr>
          </thead>

          <tbody>
            {module.permissions.map((permission) => (
              <tr
                key={permission.key}
                className="border-b border-slate-100 transition last:border-b-0 hover:bg-slate-50"
              >
                <td className="px-3 py-2 align-top">
                  <Checkbox
                    id={`${idPrefix}-${permission.key}`}
                    name={`${idPrefix}-${permission.key}`}
                    checked={checked.has(permission.key)}
                    onChange={(e) => onSetKeys([permission.key], e.target.checked)}
                    disabled={disabled}
                  />
                </td>

                <td className="px-3 py-2 align-top">
                  <label
                    htmlFor={`${idPrefix}-${permission.key}`}
                    className="block cursor-pointer text-sm font-medium text-slate-800"
                  >
                    {permission.name}
                  </label>
                  {/* The key is a machine detail beside the human name: grey, not a link. */}
                  <span className="mt-0.5 block font-mono text-xs text-slate-500">
                    {permission.key}
                  </span>
                </td>

                <td className="px-3 py-2 align-top text-sm text-slate-600">
                  {permission.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
